import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '@/services/ApiService';
import {
    GET_SHOP_TERMS_CONDITIONS,
    CREATE_SHOP_TERMS_CONDITIONS,
    UPDATE_SHOP_TERMS_CONDITIONS,
    DELETE_SHOP_TERMS_CONDITIONS,
    ADD_SHOP_TERMS_SECTION,
    UPDATE_SHOP_TERMS_SECTION,
    DELETE_SHOP_TERMS_SECTION,
    ADD_SHOP_TERMS_SUB_SECTION,
    UPDATE_SHOP_TERMS_SUB_SECTION,
    DELETE_SHOP_TERMS_SUB_SECTION,
} from '@/services/ApiRoutes';

export interface SubSection {
    id?: string;
    _id?: string;
    title: string;
    content: string;
}

export interface Section {
    id?: string;
    _id?: string;
    title: string;
    order: number;
    subSections: SubSection[];
}

export interface ShopTerms {
    _id?: string;
    id?: string;
    title: string;
    content: string;
    lastUpdated?: string;
    sections?: Section[];
    tableOfContents?: string[];
    createdAt?: string;
    updatedAt?: string;
}

interface ShopTermsState {
    data: ShopTerms | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShopTermsState = {
    data: null,
    loading: false,
    error: null,
};

export const fetchShopTermsConditions = createAsyncThunk(
    'shopTerms/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_TERMS_CONDITIONS);
            return response.data?.data || response.data || null;
        } catch (error: any) {
            if (error.response?.status === 404 || error.status === 404 || error.message?.toLowerCase().includes('not found')) {
                return null;
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch shop terms');
        }
    }
);

export const createShopTermsConditions = createAsyncThunk(
    'shopTerms/create',
    async (data: { title: string; content?: string; sections?: Section[] }, { rejectWithValue }) => {
        try {
            const response = await post(CREATE_SHOP_TERMS_CONDITIONS, data);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            if (error.response?.status === 400 || error.response?.status === 409) {
                try {
                    const putRes = await put(UPDATE_SHOP_TERMS_CONDITIONS, data);
                    return putRes.data?.data || putRes.data || putRes;
                } catch (putErr: any) {
                    return rejectWithValue(putErr.response?.data?.message || 'Failed to save shop terms');
                }
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create shop terms');
        }
    }
);

export const updateShopTermsConditions = createAsyncThunk(
    'shopTerms/update',
    async (data: { title?: string; content?: string; sections?: Section[] }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_TERMS_CONDITIONS, data);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            if (error.response?.status === 404 || error.response?.status === 400) {
                try {
                    const postRes = await post(CREATE_SHOP_TERMS_CONDITIONS, data as any);
                    return postRes.data?.data || postRes.data || postRes;
                } catch (postErr: any) {
                    return rejectWithValue(postErr.response?.data?.message || 'Failed to update shop terms');
                }
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update shop terms');
        }
    }
);

export const deleteShopTermsConditions = createAsyncThunk(
    'shopTerms/delete',
    async (_, { rejectWithValue }) => {
        try {
            await del(DELETE_SHOP_TERMS_CONDITIONS);
            return null;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete shop terms');
        }
    }
);

export const addShopTermsSection = createAsyncThunk(
    'shopTerms/addSection',
    async (data: { title: string; order?: number; subSections?: SubSection[] }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_SHOP_TERMS_SECTION, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add section');
        }
    }
);

export const updateShopTermsSection = createAsyncThunk(
    'shopTerms/updateSection',
    async ({ sectionId, data }: { sectionId: string; data: { title?: string; order?: number } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_TERMS_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update section');
        }
    }
);

export const deleteShopTermsSection = createAsyncThunk(
    'shopTerms/deleteSection',
    async (sectionId: string, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_TERMS_SECTION(sectionId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete section');
        }
    }
);

export const addShopTermsSubSection = createAsyncThunk(
    'shopTerms/addSubSection',
    async ({ sectionId, data }: { sectionId: string; data: { title: string; content: string } }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_SHOP_TERMS_SUB_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add sub-section');
        }
    }
);

export const updateShopTermsSubSection = createAsyncThunk(
    'shopTerms/updateSubSection',
    async ({ sectionId, subId, data }: { sectionId: string; subId: string; data: { title?: string; content?: string } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_TERMS_SUB_SECTION(sectionId, subId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update sub-section');
        }
    }
);

export const deleteShopTermsSubSection = createAsyncThunk(
    'shopTerms/deleteSubSection',
    async ({ sectionId, subId }: { sectionId: string; subId: string }, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_TERMS_SUB_SECTION(sectionId, subId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete sub-section');
        }
    }
);

const shopTermsSlice = createSlice({
    name: 'shopTerms',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShopTermsConditions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShopTermsConditions.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchShopTermsConditions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createShopTermsConditions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createShopTermsConditions.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(createShopTermsConditions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateShopTermsConditions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShopTermsConditions.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(updateShopTermsConditions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(deleteShopTermsConditions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteShopTermsConditions.fulfilled, (state) => {
                state.loading = false;
                state.data = null;
            })
            .addCase(deleteShopTermsConditions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Section & Sub-Section reducers
            .addCase(addShopTermsSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateShopTermsSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteShopTermsSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(addShopTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateShopTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteShopTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            });
    },
});

export const { clearError } = shopTermsSlice.actions;
export default shopTermsSlice.reducer;
